import * as db from './db'

/**
 * Очередь отправки.
 *
 * Задача переживает перезагрузку страницы и закрытие приложения:
 * без этого офлайн-режим сводится к «подождите, не выключайте»,
 * а водитель работает на парковке, где связь появляется рывками.
 */

export const TYPE_UPLOAD_PHOTO = 'upload_photo'
export const TYPE_START_TRIP = 'start_trip'
export const TYPE_FINISH_TRIP = 'finish_trip'
export const TYPE_CREATE_EVENT = 'create_event'
// Пачка точек трека. Отличается от прочих задач тем, что повторная
// доставка безвредна: сервер отсеивает уже известные точки
// по client_point_id.
export const TYPE_TRIP_LOCATIONS = 'trip_locations'
// Сообщение чата. Идемпотентность держит client_message_id
// в payload: повтор задачи сервер отвечает 200 с уже созданным
// сообщением, и обработчик трактует это как успех.
export const TYPE_SEND_MESSAGE = 'send_message'

export const PENDING = 'pending'
export const IN_FLIGHT = 'in_flight'
export const FAILED = 'failed'
export const DONE = 'done'

/**
 * Ретраи.
 *
 * Экспоненциально, но с потолком: без потолка задача, застрявшая
 * на сутки, начнёт повторяться раз в несколько часов и водитель
 * решит, что приложение сломалось. Пять минут — верхняя граница,
 * при которой возврат связи замечается за разумное время.
 */
const BASE_DELAY_MS = 2_000
const MAX_DELAY_MS = 5 * 60_000
export const MAX_ATTEMPTS = 8

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ?? `t-${Date.now()}-${Math.random().toString(36).slice(2)}`

export function backoffDelay(attempts) {
  const delay = BASE_DELAY_MS * 2 ** Math.max(0, attempts - 1)
  // Джиттер: без него все задачи, поставленные одновременно,
  // повторятся одновременно и создадут всплеск на сервере
  // ровно в момент возврата связи.
  const jitter = Math.random() * 0.3 + 0.85
  return Math.min(delay * jitter, MAX_DELAY_MS)
}

/**
 * Поставить задачу.
 *
 * idempotencyKey генерируется здесь и не меняется при ретраях —
 * в этом весь смысл: сервер узнаёт повтор и не создаёт дубль.
 */
export async function enqueue({ type, payload, draftId = null, dedupeKey = null }) {
  if (dedupeKey) {
    const existing = await findByDedupe(dedupeKey)
    if (existing && existing.status !== DONE) {
      // Переснял то же фото — заменяем задачу, а не добавляем вторую.
      existing.payload = payload
      existing.attempts = 0
      existing.status = PENDING
      existing.nextRetryAt = Date.now()
      existing.error = null
      await db.put(db.STORE_OUTBOX, existing)
      return existing
    }
  }

  const task = {
    id: uuid(),
    type,
    payload,
    draftId,
    dedupeKey,
    idempotencyKey: uuid(),
    // Момент нажатия, а не отправки: для офлайн-действий расходится
    // на часы, и сервер пишет оба времени.
    clientOccurredAt: new Date().toISOString(),
    attempts: 0,
    status: PENDING,
    nextRetryAt: Date.now(),
    error: null,
    createdAt: new Date().toISOString(),
  }

  await db.put(db.STORE_OUTBOX, task)
  return task
}

async function findByDedupe(dedupeKey) {
  const all = (await db.getAll(db.STORE_OUTBOX)) || []
  return all.find((t) => t.dedupeKey === dedupeKey) || null
}

/** Задачи, которым пора. */
export async function due(now = Date.now()) {
  const all = (await db.getAll(db.STORE_OUTBOX)) || []
  return all
    .filter((t) => t.status === PENDING && (t.nextRetryAt || 0) <= now)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
}

export async function all() {
  return (await db.getAll(db.STORE_OUTBOX)) || []
}

export async function pending() {
  const list = await all()
  return list.filter((t) => t.status === PENDING || t.status === IN_FLIGHT)
}

export async function markInFlight(id) {
  const task = await db.get(db.STORE_OUTBOX, id)
  if (!task) return null
  task.status = IN_FLIGHT
  await db.put(db.STORE_OUTBOX, task)
  return task
}

export async function markDone(id) {
  // Успешную задачу удаляем, а не помечаем: очередь должна быть
  // короткой, иначе её просмотр перестаёт что-либо показывать.
  await db.remove(db.STORE_OUTBOX, id)
}

/**
 * Отметить неудачу.
 *
 * Различаем «попробуем позже» и «уже не получится»: задача с битыми
 * данными будет повторяться вечно и займёт место, которое нужно
 * свежим снимкам.
 */
export async function markFailed(id, error, { permanent = false } = {}) {
  const task = await db.get(db.STORE_OUTBOX, id)
  if (!task) return null

  task.attempts = (task.attempts || 0) + 1
  task.error = String(error || 'Неизвестная ошибка').slice(0, 300)

  if (permanent || task.attempts >= MAX_ATTEMPTS) {
    task.status = FAILED
    task.nextRetryAt = null
  } else {
    task.status = PENDING
    task.nextRetryAt = Date.now() + backoffDelay(task.attempts)
  }

  await db.put(db.STORE_OUTBOX, task)
  return task
}

/** Ручной повтор из экрана очереди. */
export async function retry(id) {
  const task = await db.get(db.STORE_OUTBOX, id)
  if (!task) return null
  task.status = PENDING
  task.attempts = 0
  task.nextRetryAt = Date.now()
  task.error = null
  await db.put(db.STORE_OUTBOX, task)
  return task
}

export const remove = (id) => db.remove(db.STORE_OUTBOX, id)

/**
 * Вернуть зависшие задачи в очередь.
 *
 * Приложение закрыли посреди отправки — задача осталась in_flight
 * и без этого не повторится никогда.
 */
export async function recoverStuck() {
  const list = await all()
  const stuck = list.filter((t) => t.status === IN_FLIGHT)
  await Promise.all(
    stuck.map((t) => {
      t.status = PENDING
      t.nextRetryAt = Date.now()
      return db.put(db.STORE_OUTBOX, t)
    })
  )
  return stuck.length
}

export async function removeDraft(draftId) {
  const list = await all()
  const mine = list.filter((t) => t.draftId === draftId)
  await Promise.all(mine.map((t) => db.remove(db.STORE_OUTBOX, t.id)))
  return mine.length
}
